import { Country } from '@/domain/countries'
import { onboardEcommerce } from '@/api/settings-api'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import {
  ShippingRule,
  toBackendShippingConfig
} from '@/application/manage-shipping/shipping-config'
import { ShippingConfiguration } from './use-available-shipping-rules'
import { usePortalTranslate } from './use-portal-translate'
import {
  BrandInformation,
  DashboardUsers,
  OnboardingInfo,
  OnboardingPlatform,
  RefundMethod,
  RefundTiming,
  WarehouseAddress,
  onboardingContacts
} from '@/domain/onboarding'
import {
  PbDashboardUser,
  PbBrandInformation,
  PbBrandInformationPlatformEnum,
  PbCustomIntegrationParams,
  PbDashboardUsers,
  PbRefundConditions,
  PbRefundConditionsRefundMethodsEnum,
  PbRefundConditionsTimingEnum,
  PbWarehouseAddress,
  PbReturnAmountRule,
  SettingsApiOnboardRequest
} from '@itsrever/rever-api'
import { toApiContacts } from '@/infrastructure/api/ecommerce-contacts'

export function useOnboardEcommerce(
  countries: Country[],
  ecommerceId: string,
  availableConfiguration: ShippingConfiguration,
  reasonKeys: string[],
  onboardingInfo: OnboardingInfo,
  urlParams: Record<string, string>
) {
  // The translator useAvailablePublicShippingRules labels the reasons with, so they map back.
  const { transDefault } = usePortalTranslate({})
  const { t } = useTranslation()

  const [loadingNewShippingConfig, setLoadingNewShippingConfig] =
    useState(false)

  async function onboard(rules: ShippingRule[]): Promise<boolean> {
    try {
      setLoadingNewShippingConfig(true)
      const shippingConfig = toBackendShippingConfig(
        rules,
        availableConfiguration,
        countries,
        transDefault,
        reasonKeys
      )
      await onboardEcommerce(
        toOnboardingRequest(
          ecommerceId,
          onboardingInfo,
          shippingConfig,
          urlParams
        )
      )
      return true
    } catch (error) {
      console.warn(error)
      toast({ text: t('onboarding.error'), variant: 'error' })
      return false
    } finally {
      setLoadingNewShippingConfig(false)
    }
  }

  return { onboard, loadingNewShippingConfig }
}

function toOnboardingRequest(
  ecommerceId: string,
  onboardingInfo: OnboardingInfo,
  rules: PbReturnAmountRule[],
  urlParams: Record<string, string>
): SettingsApiOnboardRequest {
  return {
    pbOnboardingRequest: {
      brand_information: toSettingsBrandInformation(
        onboardingInfo.brandInformation
      ),
      dashboard_users: toSettingsDashboardUser(onboardingInfo.dashboardUsers),
      ecommerce_id: ecommerceId,
      return_amounts: { ecommerce_id: ecommerceId, rules },
      return_conditions: {
        return_period_days: onboardingInfo.returnConditions.returnDays ?? 0
      },
      warehouse_address: toSettingsWarehouseAddress(
        onboardingInfo.warehouseAddress
      ),
      refund_conditions: toRefundConditions(onboardingInfo),
      custom_integration_params: toCustomIntegrationParams(
        urlParams,
        onboardingInfo.brandInformation
      ),
      contacts: toApiContacts(
        onboardingContacts(onboardingInfo.dashboardUsers.users)
      )
    }
  }
}

function toSettingsBrandInformation(
  brandInformation: BrandInformation
): PbBrandInformation {
  return {
    ecommerce_name: brandInformation.ecommerceName,
    group: brandInformation.group ?? undefined,
    platform: toBackendPlatform(brandInformation.platform),
    restocking_available: brandInformation.restock
  }
}

export function toBackendPlatform(
  platform: OnboardingPlatform
): PbBrandInformationPlatformEnum {
  switch (platform) {
    case 'Shopify':
      return 'SHOPIFY'
    case 'Shopify Plus':
      return 'SHOPIFY'
    case 'Prestashop':
      return 'PRESTASHOP'
    case 'Magento':
      return 'MAGENTO_V2'
    case 'WooCommerce':
      return 'WOOCOMMERCE'
    case 'Shopware':
      return 'SHOPWARE'
    case 'Custom platform v2':
      return 'CUSTOM_INTEGRATION_V2'
    case 'Commerce Layer':
      return 'COMMERCELAYER'
    // Deliberately generic: these onboard as CUSTOM_GENERIC today. Listed rather
    // than left to `default` so the exhaustiveness check below can exist.
    case 'Salesforce':
    case 'LogiCommerce':
    case 'TikTok Shop':
    case 'Custom platform':
    case 'Inditex':
    case undefined:
      return 'CUSTOM_GENERIC'
    default:
      // A new OnboardingPlatform that nobody mapped is a COMPILE ERROR here.
      // Previously it fell through to CUSTOM_GENERIC, so the merchant onboarded
      // as the wrong platform — succeeding, silently, with nothing to catch it.
      return assertNeverPlatform(platform)
  }
}

// Returns `never`: it always throws, and typing it so models the control flow
// honestly rather than pretending a value comes back.
function assertNeverPlatform(platform: never): never {
  throw new Error(`unmapped onboarding platform: ${String(platform)}`)
}

function toSettingsDashboardUser(
  dashboardUsers: DashboardUsers
): PbDashboardUsers {
  const users: PbDashboardUser[] = dashboardUsers.users.map((user) => ({
    email: user.email,
    role: user.role === 'admin' ? 'ROLE_WRITE_ALL' : 'ROLE_READ_ONLY'
  }))

  return {
    users
  }
}

function toSettingsWarehouseAddress(
  warehouseAddress: WarehouseAddress
): PbWarehouseAddress {
  return {
    address_line_1: warehouseAddress.addressLine1,
    address_line_2: warehouseAddress.addressLine2,
    city: warehouseAddress.city,
    company: warehouseAddress.company,
    country_code: warehouseAddress.countryCode,
    email: warehouseAddress.email,
    name: warehouseAddress.name,
    phone: warehouseAddress.phone,
    postal_code: warehouseAddress.postcode,
    province: warehouseAddress.province,
    country: warehouseAddress.country
  }
}

function toRefundConditions(
  onboardingData: OnboardingInfo
): PbRefundConditions {
  return {
    exchange:
      onboardingData.returnConditions.refundMethods.includes('EXCHANGE'),
    fallback_bank_transfer:
      onboardingData.returnConditions.fallbackBankTransfer ?? false,
    timing: toBackendTiming(onboardingData.returnConditions.timing),
    refund_methods: toRefundMethods(
      onboardingData.returnConditions.refundMethods
    )
  }
}

function toRefundMethods(
  methods: RefundMethod[]
): PbRefundConditionsRefundMethodsEnum[] {
  const filteredMethods = methods.filter((method) => method !== 'EXCHANGE')
  if (filteredMethods.length) {
    const backendMethods: PbRefundConditionsRefundMethodsEnum[] =
      filteredMethods.map((method) => {
        switch (method) {
          case 'BANK_TRANSFER':
            return 'BANK_TRANSFER'
          case 'ORIGINAL':
            return 'ORIGINAL'
          case 'PROMO_CODE':
            return 'PROMO_CODE'
          case 'GIFT_CARD':
            return 'GIFT_CARD'
          default:
            return 'NO_PAYMENT_METHOD'
        }
      })
    return backendMethods
  }
  return []
}

function toBackendTiming(timing: RefundTiming): PbRefundConditionsTimingEnum {
  switch (timing) {
    case 'ON_START_RETURN_PROCESS':
      return 'ON_START_RETURN_PROCESS'
    case 'ON_ITEM_SENT':
      return 'ON_ITEM_SENT'
    default:
      return 'ON_ITEM_VERIFIED'
  }
}

function toCustomIntegrationParams(
  params: Record<string, string>,
  brandInformation: BrandInformation
): PbCustomIntegrationParams {
  const subplatform = getCustomIntegrationSubplatform(brandInformation.platform)

  if (!subplatform) return {}

  switch (subplatform) {
    case CustomSubplatform.Tiktok:
      return {
        subplatform: CustomSubplatform.Tiktok,
        tiktok_app_code: params.code,
        tiktok_app_key: params.app_key,
        tiktok_shop_id: brandInformation.tiktokShopId
      }
  }

  return {}
}

function getCustomIntegrationSubplatform(
  onboardingPlatform: OnboardingPlatform
): CustomSubplatform | null {
  switch (onboardingPlatform) {
    case 'TikTok Shop':
      return CustomSubplatform.Tiktok
    case 'LogiCommerce':
      return CustomSubplatform.Logicommerce
    case 'Custom platform':
      return CustomSubplatform.OwnPlatform
    default:
      return null
  }
}

enum CustomSubplatform {
  Tiktok = 'TIKTOK',
  Logicommerce = 'LOGICOMMERCE',
  OwnPlatform = 'OWN_PLATFORM',
  Mirakl = 'MIRAKL',
  Vtex = 'VTEX'
}
